import { createFileRoute } from "@tanstack/react-router";
import { GridSkeleton } from "@/components/shared/Skeletons";
import { Award, CheckCircle2 } from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import EmptyState from "@/components/shared/EmptyState";
import { Badge } from "@/components/ui/badge";
import { useStudentBadges } from "@/hooks/useBadges";

export const Route = createFileRoute("/student/badges/")({
  component: StudentBadges,
});

function getBadgeMeta(record: any) {
  const badge = record.badge || record.Badge || record;
  return {
    id: record.id || badge.id,
    name: badge.name || badge.title || "Achievement",
    description: badge.description || "Earned from your verified platform activity.",
    category: badge.criteria_type || badge.category || "general",
    awardedAt: record.awarded_at || record.created_at || badge.awarded_at,
  };
}

function StudentBadges() {
  const { data: rawBadges = [], isLoading } = useStudentBadges();
  const badges = Array.isArray(rawBadges) ? rawBadges.map(getBadgeMeta) : [];

  return (
    <div>
      <PageHeader
        title="My badges"
        subtitle="Achievements earned from verified work logs, tips, ratings, and platform milestones."
        breadcrumbs={[{ label: "Dashboard", path: "/student/dashboard" }, { label: "Badges" }]}
      />

      {isLoading ? (
        <GridSkeleton cards={8} />
      ) : badges.length === 0 ? (
        <EmptyState
          icon={<Award />}
          title="No badges earned yet"
          description="Badges will appear here once the API awards them from real work logs and reviews."
        />
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {badges.map((badge) => (
            <li
              key={badge.id}
              className="flex h-full flex-col items-start gap-3 rounded-md border border-border bg-card p-4 transition-colors hover:border-hairline-strong md:p-5"
            >
              <span
                className="flex size-10 items-center justify-center rounded-sm border border-success/30 bg-success/10 text-success"
                aria-hidden
              >
                <Award className="size-5" />
              </span>
              <div className="min-w-0">
                <h3 className="text-sm font-medium text-foreground">{badge.name}</h3>
                <p className="mt-1 text-[13px] leading-5 text-muted-foreground">
                  {badge.description}
                </p>
              </div>
              <div className="mt-auto flex flex-wrap gap-1.5">
                <Badge variant="outline" className="capitalize">
                  {badge.category}
                </Badge>
                {badge.awardedAt && (
                  <Badge variant="success">
                    <CheckCircle2 aria-hidden />
                    {new Date(badge.awardedAt).toLocaleDateString()}
                  </Badge>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
